import { useCurrency } from "../../../Hooks/useCurrency";
import { Card, CardContent } from "../../../Components/UI/card";
import { Building2, Camera, Crown, Upload, ChevronDown, Check, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import apiService from "../../../Services/APIService";
import { useDispatch, useSelector } from "react-redux";
import { setGlobalLoading } from "../../../Store/Actions";
import { isLive, isPremium, SCRM_LOGIN_MODE, getUploadUrl } from "../../../Utils/Utilities";
import { getStorageData } from "../../../Utils/Storage";
import { updateCompanyProfile } from "../../../Modules/Settings/Profile/ProfileSectionAPI";
import { getAuthData } from "../../../Utils/AuthUtils";
import { checkIfExists } from "../../../Services/LoginAPI";
import { Button } from "../../../Components/UI/button";
import UpgradeSubscription from "../../../Modules/Settings/UpgradeSubscription/UpgradeSubscription";
import { useNavigate, useLocation } from "react-router-dom";
import { checkIfInqOnlyMode } from "../../../Store/Reducers";
import ThreadSahiIcon from "../../../Components/Common/ThreadSahiIcon";
import QuickTraining from "../../../Components/QuickTraining/QuickTraining";
import { getTrainingData } from "../../../Components/QuickTraining/QuickTrainingData";
import AgencySelector from "../../../Components/Common/AgencySelector";
import { compressImage, cropToCenterSquare } from "../../../Utils/ImageCompressor";
import { switchAgency } from "../../../Utils/AgencySwitch";

const DashboardHeader = ({
  dashboardHeaderData = {
    agencyID: null,
    agencyName: "",
    logoPath: "",
    metrics: []
  },
  isAdmin = false,
  isMobile = false,
  // When true, the agency switcher only lists profiles where the current user is that
  // agency's admin (AdminUserID === UserID) - used on the Settings page. Dashboard usage
  // leaves this false so every agency the user belongs to still shows there.
  filterAdminOnly = false,
  navigateOnSwitch = true,
}) => {
  // Local state variables
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const fileInputRef = useRef(null);
  const currencySymbol = useCurrency();
  const dispatch = useDispatch();
  const [upgradeOpen, setIsUpgradeOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  const isPremiumUser = isPremium();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrollToPending, setScrollToPending] = useState(false);
  const [userAgencies, setUserAgencies] = useState([]);
  const authData = getAuthData();

  const pendingAgencies = userAgencies?.filter(a => Number(a.Status) === 0) || [];
  const pendingCount = pendingAgencies.length;

  // Only affects what's shown in the switcher - handleAgencySwitch/localStorage caching below
  // still operate on the full userAgencies list regardless of this filter.
  // "Admin" here means either the creator (Agencies.AdminUserID) or a user with the Users.IsAdmin
  // bit set for that agency - the 'U' table (synced via usr_GetUpdatePkg) carries the per-agency
  // IsAdmin bit, while MyAgencies (usr_CheckIfExists) only carries AdminUserID (the creator).
  const visibleAgencies = filterAdminOnly
    ? userAgencies.filter((a) => {
      if (Number(a.Status) === 0) return true;
      if (Number(a.AdminUserID) === Number(a.UserID)) return true;
      const storedUsers = getStorageData('U') || [];
      const userRow = storedUsers.find((u) => Number(u.UserID) === Number(a.UserID));
      return userRow?.IsAdmin === true || userRow?.IsAdmin === 1;
    })
    : userAgencies;

  useEffect(() => {
    if (SCRM_LOGIN_MODE === "MultiAgency") {
      // Try to load from localStorage first
      const storedAgenciesRaw = localStorage.getItem('MyAgencies');
      if (storedAgenciesRaw) {
        try {
          const stored = JSON.parse(storedAgenciesRaw);
          if (Array.isArray(stored) && stored.length > 0) {
            setUserAgencies(stored);
            return;
          }
        } catch (e) {
          console.error("Error parsing stored MyAgencies:", e);
        }
      }

      if (authData?.Mobile) {
        const fetchAgencies = async () => {
          try {
            const result = await checkIfExists(authData.Mobile, authData.CC);
            if (result?.Table1) {
              // Strip any row-tagging "Tbl" field so this matches what APIService's sync path
              // caches under the same 'MyAgencies' key (see APIService.js updateBizLocalData()).
              const cleanedAgencies = result.Table1.map(({ Tbl, ...rest }) => rest);
              setUserAgencies(cleanedAgencies);
              localStorage.setItem('MyAgencies', JSON.stringify(cleanedAgencies));
            }
          } catch (error) {
            console.error("Failed to fetch profiles for switcher:", error);
          }
        };
        fetchAgencies();
      }
    }
  }, [authData?.Mobile, authData?.CC]);

  const clearAllStores = () => {
    return new Promise((resolve) => {
      const request = indexedDB.open('SahiCRMIDB', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const stores = ['Conns', 'Threads', 'Contracts', 'Tasks'];
        try {
          const tx = db.transaction(stores, 'readwrite');
          stores.forEach(store => {
            if (db.objectStoreNames.contains(store)) {
              tx.objectStore(store).clear();
            }
          });
          tx.oncomplete = () => {
            db.close();
            resolve();
          };
          tx.onerror = () => {
            db.close();
            resolve();
          };
        } catch (e) {
          db.close();
          resolve();
        }
      };
      request.onerror = () => {
        resolve();
      };
    });
  };

  const handleAgencySwitch = async (targetAgencyID) => {
    if (Number(targetAgencyID) === Number(dashboardHeaderData.agencyID)) return;
    setDropdownOpen(false);
    dispatch(setGlobalLoading(true));
    try {
      const success = await switchAgency(targetAgencyID, dispatch);
      if (!success) {
        throw new Error("Target agency credentials not found locally.");
      }

      // Cleanly navigate to home/dashboard if enabled and not staying on settings/profile page
      if (navigateOnSwitch && location.pathname !== "/settings") {
        navigate("/");
      }

      dispatch(setGlobalLoading(false));

    } catch (error) {
      console.error("Switch failed:", error);
      dispatch(setGlobalLoading(false));
    }
  };

  // Function which will handle logo upload with new API
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file");
      return;
    }

    if (!dashboardHeaderData.agencyID || !dashboardHeaderData.agencyName) {
      alert("Agency ID and Agency Name are required to upload logo");
      return;
    }

    dispatch(setGlobalLoading(true));

    try {
      // Automatically crop to maximum possible centered square, then compress
      const squareFile = await cropToCenterSquare(file, 800, 0.9);
      const fileToUpload = await compressImage(squareFile, 800, 0.85);

      if (fileToUpload.size > 2 * 1024 * 1024) {
        alert("File size should be less than 2MB");
        return;
      }

      const basePath = isLive()
        ? "Profile" //"https://sahicrm.com/Uploads/"
        : "Profile";

      // Step 1: Upload the logo file
      const uploadResult = await apiService.uploadFile(
        fileToUpload,
        basePath
      );

      if (uploadResult.error) {
        alert(uploadResult.message);
        return;
      }

      const { fileName, relativePath } = uploadResult.data;

      // Step 2: Update profile with logo relative path
      const payload = {
        changeType: "AGNLOGO",
        companyName: dashboardHeaderData.agencyName,
        stateID: 0,
        taxID: "",
        logo: relativePath, // Store the relative path
        banner: ""
      };

      const result = await updateCompanyProfile('AGNLOGO', payload);

      // If successful, update UI
      if (result && result.error === false && result.message === "Success") {

        // Update the logo URL for display - construct the URL based on environment
        const logoUrl = getUploadUrl(`Uploads/${relativePath}`);
        setLogoUrl(logoUrl);
        setLogoError(false);

        // Also update local storage and state for userAgencies so it updates instantly on mobile:
        const currentAgencyID = dashboardHeaderData.agencyID || authData?.AgencyID;
        const updatedAgencies = userAgencies.map(agency => {
          if (Number(agency.AgencyID) === Number(currentAgencyID)) {
            return { ...agency, Logo: relativePath };
          }
          return agency;
        });
        setUserAgencies(updatedAgencies);
        localStorage.setItem('MyAgencies', JSON.stringify(updatedAgencies));

        console.log("Logo uploaded and profile updated.");
      } else {
        alert("Logo uploaded but failed to update profile. Please refresh the page.");
      }

    } catch (error) {
      console.error("Error uploading logo:", error);
      alert("Failed to upload logo. Please try again.");
    } finally {
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      dispatch(setGlobalLoading(false));
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Load existing logo if available and synchronize it with MyAgencies
  useEffect(() => {
    if (dashboardHeaderData.agencyID && dashboardHeaderData.agencyName) {
      if (dashboardHeaderData.logoPath) {
        const logoUrl = getUploadUrl(`Uploads/${dashboardHeaderData.logoPath}`);
        setLogoUrl(logoUrl);
      } else {
        setLogoUrl(null);
      }

      // Sync active agency logo back to MyAgencies array in localStorage
      const storedAgenciesRaw = localStorage.getItem('MyAgencies');
      if (storedAgenciesRaw) {
        try {
          const stored = JSON.parse(storedAgenciesRaw);
          if (Array.isArray(stored)) {
            let updated = false;
            const nextAgencies = stored.map(agency => {
              if (Number(agency.AgencyID) === Number(dashboardHeaderData.agencyID)) {
                if (agency.Logo !== dashboardHeaderData.logoPath) {
                  updated = true;
                  return { ...agency, Logo: dashboardHeaderData.logoPath };
                }
              }
              return agency;
            });
            if (updated) {
              localStorage.setItem('MyAgencies', JSON.stringify(nextAgencies));
              setUserAgencies(nextAgencies);
            }
          }
        } catch (e) {
          console.error("Error updating MyAgencies with active logo:", e);
        }
      }
    }
  }, [dashboardHeaderData.agencyID, dashboardHeaderData.agencyName, dashboardHeaderData.logoPath]);

  // Handle logo load error
  const handleLogoError = () => {
    console.log("Logo failed to load, showing default icon");
    setLogoError(true);
    setLogoUrl(null);
  };

  // Render desktop layout - always full width with conditional subscription button
  const renderDesktopLayout = () => {
    return (
      <Card
        className="transition-all duration-300 hover:shadow-lg md:rounded-xl"
        style={{
          backgroundColor: "var(--bg-white)",
          border: "1px solid var(--sahi-border-color)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <CardContent className="p-4 sm:p-6 4xl:pt-3 sm:pt-2">
          <div className="flex items-center justify-between gap-6">
            {/* Left: Logo and Title */}
            <div className="flex items-center gap-3">
              <div
                className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center shadow-sm ${isAdmin ? 'group cursor-pointer' : 'cursor-default'}`}
                style={{ backgroundColor: (logoUrl && !logoError) ? "transparent" : "var(--sahi-green)" }}
                onClick={isAdmin ? triggerFileInput : undefined}
                title={isAdmin ? "Click to upload logo" : ""}
              >
                {logoUrl && !logoError ? (
                  <>
                    <img
                      src={logoUrl}
                      alt="Company Logo"
                      className="w-full h-full object-cover rounded-xl"
                      onError={handleLogoError}
                    />
                    {isAdmin && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <Camera className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Building2 className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: "var(--sahi-white)" }} />
                    {isAdmin && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <Upload className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
              <div className="relative">
                {SCRM_LOGIN_MODE === "MultiAgency" && visibleAgencies.length >= 1 ? (
                  <div className="flex flex-col">
                    {/* Trigger */}
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <div
                        className="flex items-center gap-2 cursor-pointer select-none group"
                        onClick={() => {
                          setScrollToPending(false);
                          setDropdownOpen(true);
                        }}
                      >
                        <h1 className="text-xl sm:text-2xl font-bold group-hover:text-sahi-green transition-colors" style={{ color: "var(--text-color)" }}>
                          {dashboardHeaderData.agencyName}
                        </h1>
                        <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-sahi-green transition-colors mt-1" />
                      </div>

                      {pendingCount > 0 && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setScrollToPending(true);
                            setDropdownOpen(true);
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-semibold bg-background hover:bg-zinc-200/80 text-zinc-700 border border-zinc-200 cursor-pointer transition-all select-none shadow-2xs hover:shadow-xs group"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-sahi-primary-color shrink-0 animate-pulse"></span>
                          <span>{pendingCount} Pending Invitation{pendingCount > 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>

                    <AgencySelector
                      open={dropdownOpen}
                      onOpenChange={(isOpen) => {
                        setDropdownOpen(isOpen);
                        if (!isOpen) setScrollToPending(false);
                      }}
                      userAgencies={visibleAgencies}
                      currentAgencyID={dashboardHeaderData.agencyID}
                      handleAgencySwitch={handleAgencySwitch}
                      scrollToPending={scrollToPending}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-bold" style={{ color: "var(--text-color)" }}>
                      {dashboardHeaderData.agencyName}
                    </h1>
                    {pendingCount > 0 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setScrollToPending(true);
                          setDropdownOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-zinc-100 hover:bg-zinc-200/80 text-zinc-700 border border-zinc-200/80 cursor-pointer transition-all select-none shadow-2xs hover:shadow-xs group"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-sahi-primary-color shrink-0 animate-pulse"></span>
                        <span>{pendingCount} Pending Invitation{pendingCount > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                )}
                <p className="text-sm sm:text-base mt-1" style={{ color: "var(--sub-text-color)" }}>
                  Business Growth Console
                </p>
              </div>
            </div>

            {/* Right: Conditional Subscription Button */}

            <div className="gap-2 justify-between hidden sm:flex">
              {isInqOnlyMode && <div>
                {/* <QuickTraining 
                    trainingData={getTrainingData("TRAINING1")}
                  /> */}
              </div>}
              <div>
                {isAdmin && false && !isPremiumUser && (<Button
                  onClick={() => setIsUpgradeOpen(true)}
                  variant="sahiGreen"
                  size="lg"
                  className="gap-1"
                >
                  <Crown className="h-5 w-5 mr-2" />
                  <span className="text-lg">Buy Now</span>
                </Button>)}
              </div>
            </div>

          </div>
        </CardContent>
      </Card>
    );
  };

  // Render mobile metrics based on inquiry-only mode
  const renderMobileMetrics = () => {
    if (!isInqOnlyMode) {
      // Original mobile layout for non-inquiry-only mode
      return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4">
          {dashboardHeaderData.metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-bg-grey rounded-lg p-3 text-center"
            >
              <div className="text-lg font-bold" style={{ color: metric.color }}>
                {metric.labelM === "Avg Order" && (
                  <span className="text-sm text-sahi-grey mr-1">{currencySymbol}</span>
                )}
                {metric.value}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--sub-text-color)" }}>
                {metric.labelM}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // New mobile layout for inquiry-only mode
    return (
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 px-4">
        {dashboardHeaderData.metrics.map((metric, index) => (
          <div
            key={index}
            className={`bg-bg-grey rounded-lg p-3 text-center ${isInqOnlyMode ? 'cursor-pointer' : 'cursor-default'} hover:bg-gray-100 transition-colors duration-200`}
          // onClick={() => isInqOnlyMode && handleMetricClick(metric.label)}
          >
            <div className="text-lg font-bold" style={{ color: metric.color }}>
              {metric.labelM === "Avg Order" && (
                <span className="text-sm text-sahi-grey mr-1">{currencySymbol}</span>
              )}
              {metric.value}
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--sub-text-color)" }}>
              {metric.labelM}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Mobile Agency Switcher - rendered when isMobile prop is passed
  if (isMobile) {
    if (!userAgencies || userAgencies.length === 0) return null;

    const currentAgencyName = dashboardHeaderData.agencyName || authData?.AgencyName || '';
    const currentAgencyID = dashboardHeaderData.agencyID || authData?.AgencyID;
    const activeAgency = userAgencies.find(a => Number(a.AgencyID) === Number(currentAgencyID));
    const currentRole = activeAgency?.Designation;
    const activeAgencyLogoUrl = (logoUrl && !logoError) ? logoUrl : null;

    const hasMultipleAgencies = visibleAgencies.length >= 1;

    return (
      <div className="px-4">
        <div className="bg-white rounded-xl border border-gray-200/70 shadow-2xs overflow-hidden">
          {/* Agency Name + Chevron — opens Drawer */}
          <div
            className={`flex items-center justify-between px-3.5 py-3 select-none gap-3 bg-white hover:bg-gray-50/70 active:bg-gray-100/70 transition-colors ${hasMultipleAgencies ? "cursor-pointer" : ""
              }`}
            onClick={hasMultipleAgencies ? () => { setScrollToPending(false); setDropdownOpen(true); } : undefined}
          >
            {/* Left Branded Icon — shows logo if available */}
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 overflow-hidden border border-sahi-green/10 relative ${isAdmin ? 'cursor-pointer group' : ''}`}
              style={{ backgroundColor: activeAgencyLogoUrl ? 'transparent' : 'var(--sahi-green-light)' }}
              onClick={isAdmin ? (e) => { e.stopPropagation(); triggerFileInput(); } : undefined}
              title={isAdmin ? "Click to upload logo" : ""}
            >
              {activeAgencyLogoUrl ? (
                <>
                  <img
                    src={activeAgencyLogoUrl}
                    alt={currentAgencyName}
                    className="w-full h-full object-cover"
                    onError={handleLogoError}
                  />
                  {isAdmin && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                  )}
                </>
              ) : null}
              <Building2
                className="w-5 h-5 text-sahi-green"
                style={{ display: activeAgencyLogoUrl ? 'none' : 'block' }}
              />
              {isAdmin && !activeAgencyLogoUrl && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <Upload className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </div>

            {/* Middle Text Details */}
            <div className="min-w-0 flex-1">
              <p className="text-text-color text-lg sm:text-xl font-semibold truncate">
                {currentAgencyName}
              </p>
              <p className="text-[12px] font-medium text-zinc-400">
                Business Growth Console
              </p>
            </div>

            {/* Right Chevron Down */}
            {hasMultipleAgencies && <ChevronDown className="w-5 h-5 shrink-0 text-zinc-400" />}
          </div>

          {/* Pending Invitations Prompt Line */}
          {pendingCount > 0 && (
            <div
              className="px-3.5 py-2.5 border-t border-zinc-100/80 bg-zinc-50/70 hover:bg-zinc-100 active:bg-zinc-200/70 flex items-center cursor-pointer transition-colors select-none group"
              onClick={() => {
                setScrollToPending(true);
                setDropdownOpen(true);
              }}
            >
              <p className="text-xs font-semibold text-sahi-primary-color flex items-center gap-2 group-active:opacity-80">
                <span className="w-2 h-2 rounded-full bg-sahi-primary-color shrink-0 animate-pulse"></span>
                You have {pendingCount} pending invitation{pendingCount > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        {/* Mobile Agency Selector — custom TaskCategoryManager-like drawer */}
        {hasMultipleAgencies && (
          <AgencySelector
            open={dropdownOpen}
            onOpenChange={(isOpen) => {
              setDropdownOpen(isOpen);
              if (!isOpen) setScrollToPending(false);
            }}
            userAgencies={visibleAgencies}
            currentAgencyID={currentAgencyID}
            handleAgencySwitch={handleAgencySwitch}
            scrollToPending={scrollToPending}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg"
          onChange={handleLogoUpload}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="col-span-full">
      {/* Desktop View */}
      <div className="hidden md:block">
        {renderDesktopLayout()}
      </div>

      {/* Mobile View - Header with Logo and Title */}
      <div className="block md:hidden mb-3 bg-bg-grey p-2 py-4">
        <div className="flex items-center gap-3 px-2">
          <div>
            <h1 className="text-lg font-bold" style={{ color: "var(--text-color)" }}>
              {dashboardHeaderData.agencyName}
            </h1>
            <p className="text-sm" style={{ color: "var(--sub-text-color)" }}>
              Business Growth Console
            </p>
          </div>
        </div>
      </div>

      {/* Mobile View - 2x2 Grid Metric Boxes */}
      {isAdmin && (
        <div className="block md:hidden">
          {renderMobileMetrics()}
        </div>
      )}

      {/* Contract dialog */}
      <UpgradeSubscription
        open={upgradeOpen}
        onClose={setIsUpgradeOpen}
      />
    </div>
  );
};

export default DashboardHeader;